import { useCallback, useEffect, useMemo, useState } from "react";
import { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import { pluralizeWithCount } from "@common/helpers/string";
import { OrganizationIntegrationAccountItem } from "@common/schemas/organization-connection-groups.schema";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import {
    Modal,
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { HStack } from "@design-system/HStack/HStack";
import { InstitutionIcon } from "@design-system/InstitutionIcon/InstitutionIcon";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { itemRowId } from "@design-system/ViewTable/grouping/groupByRowTypes";
import {
    useOrgConnectionGroupItems,
    useOrgIntegrationWorkspaces,
} from "@api/organizationIntegration.api";
import {
    OrganizationIntegrationsContext,
    useOrganizationIntegrations,
} from "./OrganizationIntegrationsContext";
import { PostConnectBulkActions } from "./PostConnectBulkActions";
import {
    PostConnectAssignmentColumn,
    postConnectAssignmentView,
    postConnectAssignmentViewConfig,
} from "./postConnectAssignmentViewConfig";
import { getAccountItemRowId } from "./integrationsRowId";
import { selectedAssignmentTargets } from "./selectedAssignmentTargets";
import { useAssignmentStaging } from "./useAssignmentStaging";
import {
    assignmentCapReason,
    useCommitAssignments,
} from "./useCommitAssignments";
import styles from "./IntegrationsPage.module.scss";

interface PostConnectAssignmentModalProps {
    organizationId: string;
    /** Unified connection id — the `login` group the new accounts sit in. */
    connectionId: string;
    /**
     * Drives the close animation: the overlay keeps this mounted while the
     * dismissal plays out and flips this to false, rather than unmounting it.
     */
    show: boolean;
    onClose: () => void;
}

/**
 * The accounts of a just-made organization-level connection, listed so each one
 * can be given a client and an entity before the user leaves the flow.
 *
 * Nothing reaches the server until Confirm: picks accumulate in staging and go
 * out as one batch, so abandoning a half-finished pass leaves the accounts
 * exactly as the connect left them. Confirm stays available with rows still
 * unassigned — those just stay unassigned, and the integrations table is where
 * they get picked up later.
 *
 * Every page of the connection is pulled up front: the progress line, the
 * select-all checkbox and the batch all have to cover accounts the user hasn't
 * scrolled to.
 */
export const PostConnectAssignmentModal = ({
    organizationId,
    connectionId,
    show,
    onClose,
}: PostConnectAssignmentModalProps) => {
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const staging = useAssignmentStaging();
    const workspacesQuery = useOrgIntegrationWorkspaces(organizationId);

    const itemsQuery = useOrgConnectionGroupItems({
        organizationId,
        groupBy: "login",
        groupKey: connectionId,
        enabled: true,
    });

    const { hasNextPage, isFetchingNextPage, fetchNextPage } = itemsQuery;
    useEffect(() => {
        if (hasNextPage && !isFetchingNextPage) {
            void fetchNextPage();
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const items = useMemo(
        () => (itemsQuery.data?.pages ?? []).flatMap((page) => page.items),
        [itemsQuery.data],
    );

    const isLoaded = itemsQuery.isSuccess && !hasNextPage;
    const { pendingByAccountId } = staging;

    // An account counts as assigned once it has an entity to be assigned with,
    // staged or already persisted — the line tracks what Confirm is about to
    // achieve, not what the server currently holds.
    const assignedCount = items.filter(
        (item) =>
            item.account.entityId !== null ||
            pendingByAccountId[item.account.id]?.entityId !== undefined,
    ).length;

    const login = items[0]?.connection.login;
    const noop = useCallback(() => undefined, []);

    const assignmentContext = useMemo(
        () => ({
            organizationId,
            workspaces: workspacesQuery.data?.workspaces ?? [],
            assignmentMode: "stage" as const,
            onAssignmentsCommitted: noop,
            ...staging,
        }),
        [organizationId, workspacesQuery.data, noop, staging],
    );

    return (
        <Modal show={show} fullscreen onClose={onClose}>
            <StandardModalHeader className={styles.postConnectHeader}>
                Select client and entity
            </StandardModalHeader>
            <StandardModalBody className={styles.postConnectBody}>
                <span className={styles.postConnectProgress}>
                    {assignedCount} of {items.length} assigned
                </span>
                {login && (
                    <HStack
                        gap={8}
                        align="center"
                        className={styles.postConnectInstitution}
                    >
                        <InstitutionIcon
                            src={login.institutionLogoUrl}
                            size="sm"
                        />
                        <span>
                            {login.institutionName ??
                                login.email ??
                                "New connection"}
                        </span>
                    </HStack>
                )}
                <OrganizationIntegrationsContext.Provider
                    value={assignmentContext}
                >
                    <PostConnectAssignmentTable
                        items={items}
                        isLoaded={isLoaded}
                        rowSelection={rowSelection}
                        onRowSelectionChange={setRowSelection}
                        onClose={onClose}
                    />
                </OrganizationIntegrationsContext.Provider>
            </StandardModalBody>
        </Modal>
    );
};

interface PostConnectAssignmentTableProps {
    items: OrganizationIntegrationAccountItem[];
    isLoaded: boolean;
    rowSelection: RowSelectionState;
    onRowSelectionChange: OnChangeFn<RowSelectionState>;
    onClose: () => void;
}

/**
 * Split out so it sits inside the assignment context: sending the staged batch
 * goes through the same `useCommitAssignments` every other assignment surface
 * uses, and that hook reads the context.
 */
const PostConnectAssignmentTable = ({
    items,
    isLoaded,
    rowSelection,
    onRowSelectionChange,
    onClose,
}: PostConnectAssignmentTableProps) => {
    const { pendingByAccountId } = useOrganizationIntegrations();
    const { commit, isCommitting } = useCommitAssignments();

    const selectedRowIds = useMemo(
        () =>
            Object.entries(rowSelection)
                .filter(([, isSelected]) => isSelected)
                .map(([rowId]) => itemRowId(rowId)),
        [rowSelection],
    );

    // `selectedAssignmentTargets` resolves rows out of the table's grouped
    // state; this table has a single implicit group holding every account.
    const assignmentTargets = useMemo(
        () =>
            selectedAssignmentTargets({
                rowIds: selectedRowIds,
                childrenByKey: {
                    all: {
                        items,
                        hasMore: false,
                        isLoadingFirstPage: false,
                        isLoadingMore: false,
                        isError: false,
                        isSettled: true,
                    },
                },
                pendingByAccountId,
            }),
        [selectedRowIds, items, pendingByAccountId],
    );

    const clearSelection = useCallback(
        () => onRowSelectionChange({}),
        [onRowSelectionChange],
    );

    const stagedAssignments = items.flatMap((item) => {
        const entityId = pendingByAccountId[item.account.id]?.entityId;
        return entityId === undefined
            ? []
            : [{ financialAccountId: item.account.id, entityId }];
    });

    // `commit` drops a batch over the endpoint's cap without a toast, which
    // here would strand the user on a Confirm that does nothing. Surfaced on
    // the button instead, so an oversized connection says what to do about it.
    const blockedReason = assignmentCapReason(stagedAssignments.length);

    const handleConfirm = () => {
        if (blockedReason !== undefined) {
            return;
        }
        // Leaving with nothing picked is allowed, and there is no request to
        // make for it — the accounts stay in the table, waiting.
        if (stagedAssignments.length === 0) {
            onClose();
            return;
        }
        commit(stagedAssignments, { onSuccess: onClose });
    };

    const confirmButton = (
        <WithHotkey
            hotKey="mod+enter"
            hotKeyDisplayPosition="inline"
            tooltip={blockedReason}
            hotKeyOptions={{
                description: "Confirm account assignments",
                preventDefault: true,
            }}
        >
            {(hotkeyHint) => (
                <Button
                    className={styles.postConnectConfirm}
                    variant="primary"
                    size="md"
                    onClick={handleConfirm}
                    disabled={isCommitting || blockedReason !== undefined}
                    rightIcon={hotkeyHint}
                >
                    {isCommitting ? "Confirming..." : "Confirm"}
                </Button>
            )}
        </WithHotkey>
    );

    return (
        <ViewTable<
            OrganizationIntegrationAccountItem,
            PostConnectAssignmentColumn,
            never
        >
            data={isLoaded ? items : null}
            view={postConnectAssignmentView}
            config={postConnectAssignmentViewConfig}
            getRowId={getAccountItemRowId}
            rowSelection={rowSelection}
            onRowSelectionChange={onRowSelectionChange}
            enableRowSelection
            emptyView={() => (
                <div className={styles.emptyState}>
                    <EmptyState
                        header="No accounts to assign"
                        body="This connection landed no accounts. You can close this and try connecting again."
                    />
                </div>
            )}
            // Confirm keeps its place on the right whether or not a selection
            // is open, so the way out of the modal never moves.
            footer={
                <CommandIsland sticky>
                    {selectedRowIds.length > 0 ? (
                        <PostConnectBulkActions
                            selectedCount={selectedRowIds.length}
                            assignmentTargets={assignmentTargets}
                            onClearSelection={clearSelection}
                        />
                    ) : (
                        <CommandIslandTotal>
                            {pluralizeWithCount("account", items.length)}
                        </CommandIslandTotal>
                    )}
                    {confirmButton}
                </CommandIsland>
            }
        />
    );
};
